import React, { useState } from "react";
import emoji from './assets/MyImg/loginimg.jpeg';
import { useNavigate } from "react-router-dom";
import {
  TextField,
  Button,
  Box,
  Typography,
  Container,
  Paper,
  InputAdornment,
  IconButton,
  createTheme,
  ThemeProvider,
} from "@mui/material";
import { PersonOutline, LockOutlined, Visibility, VisibilityOff } from "@mui/icons-material";

const Login = () => {
  const [showPassword, setShowPassword] = useState(false);
  const [username, setUsername] = useState("");  // State for username
  const [password, setPassword] = useState("");  // State for password
  const navigate = useNavigate();

  const handleTogglePassword = () => {
    setShowPassword((prev) => !prev);
  };

  const handleLogin = async () => {
    if (username.trim() === "" || password.trim() === "") {
      alert("Please enter both Username and Password!");
      return;
    }

    try {
      const response = await fetch("https://enzo-matr.onrender.com/api/workers/login", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ username, password }),
      });

      if (response.ok) {
        // ✅ Navigate to Invoice page after successful login
        navigate("/invoice");
      } else {
        const errorData = await response.json();
        alert(errorData.message || "Invalid credentials");
      }
    } catch (error) {
      console.error("Error during login:", error);
      alert("An error occurred. Please try again.");
    }
  };

  // Theme with correct syntax
  const theme = createTheme({
    typography: {
      h3: { fontFamily: "'Caladea', serif" }, // Heading Font
      subtitle1: { fontFamily: "'Montserrat', sans-serif" }, // Subtitle Font
    },
  });
  return (
    <ThemeProvider theme={theme}>
      <Container
        maxWidth={false}
        disableGutters
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: "100vh",
          minWidth: "100vw",
          height: "100vh",
          width: "100vw",
          bgcolor: "#25bdbe",
          p: 0,
          m: 0,
          overflow: "hidden", // Prevents unwanted scrollbars/white space
        }}
      >
        <Paper
          elevation={4}
          sx={{
            p: { xs: 2, sm: 4 },
            display: "flex",
            flexDirection: { xs: "column", md: "row" },
            justifyContent: "center",
            alignItems: "center",
            borderRadius: 3,
            width: { xs: "98%", sm: "90%", md: "900px" },
            minWidth: 0,
            height: { xs: "auto", md: "430px" },
            bgcolor: "white",
            zIndex: 6,
            gap: { xs: 2, md: 2 },
            boxSizing: "border-box",
          }}
        >
          <Box
            sx={{
              display: "flex",
              flexDirection: { xs: "column", md: "row" },
              justifyContent: "space-between",
              alignItems: "center",
              width: "100%",
              gap: { xs: 2, md: 0 },
            }}
          >
            {/* Logo - show above form on mobile, left on desktop */}
            <Box
              sx={{
                display: { xs: "flex", md: "block" },
                justifyContent: "center",
                alignItems: "center",
                width: { xs: "100%", md: "40%" },
                mb: { xs: 2, md: 0 },
              }}
            >
              <img
                src={emoji}
                alt="Logo"
                style={{
                  width: "100%",
                  maxWidth: 200,
                  padding: 20,
                  paddingBottom: 30,
                  objectFit: "contain",
                  display: "block",
                  margin: "0 auto",
                }}
              />
            </Box>

            {/* Form */}
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                width: { xs: "100%", md: "60%" },
                minWidth: 0,
              }}
            >
              <Typography
                variant="h3"
                fontWeight="bold"
                textAlign="center"
                sx={{ marginBottom: 2, marginTop: { xs: 0, md: -3 }, fontSize: { xs: 28, sm: 32, md: 36 } }}
              >
                Login to Your Account
              </Typography>
              <Typography
                variant="subtitle1"
                fontWeight="bold"
                textAlign="center"
                sx={{
                  fontFamily: "Montserrat, sans-serif",
                  marginBottom: 4,
                  marginTop: -2,
                  fontSize: { xs: 15, sm: 16, md: 18 },
                }}
              >
                Login Using
              </Typography>
              <TextField
                placeholder="Username"
                variant="outlined"
                onChange={(e) => setUsername(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <PersonOutline sx={{ color: "white" }} />
                    </InputAdornment>
                  ),
                }}
                sx={{
                  backgroundColor: "#f7941e",
                  borderRadius: 3,
                  "& .MuiOutlinedInput-root": { borderRadius: 3 },
                  "& .MuiInputBase-input": {
                    color: "#fff",
                    fontWeight: "bold",
                    fontFamily: "Montserrat, sans-serif",
                    padding: "14px",
                    width: "100%",
                  },
                  marginBottom: 2,
                  width: { xs: "100%", sm: 350, md: 450 },
                  maxWidth: "100%",
                }}
              />
              <TextField
                placeholder="Password"
                variant="outlined"
                onChange={(e) => setPassword(e.target.value)}
                type={showPassword ? "text" : "password"}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <LockOutlined sx={{ color: "white" }} />
                    </InputAdornment>
                  ),
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton onClick={handleTogglePassword} edge="end">
                        {showPassword ? (
                          <VisibilityOff sx={{ color: "white" }} />
                        ) : (
                          <Visibility sx={{ color: "white" }} />
                        )}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
                sx={{
                  marginBottom: 5,
                  backgroundColor: "#f7941e",
                  borderRadius: 3,
                  "& .MuiOutlinedInput-root": { borderRadius: 3 },
                  "& .MuiInputBase-input": {
                    color: "white",
                    fontWeight: "bold",
                    fontFamily: "Montserrat, sans-serif",
                    padding: "14px",
                    width: "100%",
                  },
                  width: { xs: "100%", sm: 350, md: 450 },
                  maxWidth: "100%",
                }}
              />
              <Button
                variant="contained"
                sx={{
                  marginTop: 2,
                  padding: 1.5,
                  bgcolor: "#2f2f2f",
                  "&:hover": { bgcolor: "#555" },
                  width: { xs: "100%", sm: 200 },
                  height: 40,
                  borderRadius: 3,
                  fontWeight: "bold",
                  textTransform: "none",
                  fontFamily: "Poppins, sans-serif",
                  fontSize: 17,
                }}
                onClick={handleLogin}
              >
                Sign In
              </Button>
            </Box>
          </Box>
        </Paper>
        <Box
          sx={{
            position: "absolute",
            bottom: { xs: 20, md: 40 },
            left: { xs: "50%", md: "auto" },
            transform: { xs: "translateX(-50%)", md: "none" },
            width: "100%",
            display: "flex",
            justifyContent: "center"
          }}
        >
          <Button
            sx={{
              color: "white",
              fontFamily: "Poppins, sans-serif",
              fontWeight: "bold",
            }}
            onClick={() => navigate("/admin-login")}
          >
            ADMIN LOGIN
          </Button>
        </Box>
      </Container>
    </ThemeProvider>
  );
};

export default Login;